import { useState, useCallback, useRef, useMemo } from 'react';
import { useSyncRef } from './syncRef';

const FULL_BOTTOM = 0;

interface OnScreenConfig {
  bottomDistance?: number;
  onVisibilityChange?: (isVisible: boolean) => void;
}

export function useOnScreen<Element extends HTMLElement>(
  { bottomDistance = FULL_BOTTOM, onVisibilityChange }: OnScreenConfig = {
    bottomDistance: FULL_BOTTOM,
  },
): [boolean, (element: Element | null) => void] {
  const [isVisible, setIsVisible] = useState(false);

  const observer = useRef<IntersectionObserver | null>(null);
  const ref = useRef<Element | null>(null);
  const vizRef = useRef(false);

  const paramRef = useSyncRef({
    onVisibilityChange,
  });

  const setRef = useCallback(
    (element: Element | null) => {
      if (!element) return;

      if (observer.current && ref.current) observer.current?.unobserve(ref.current);

      const obs = new IntersectionObserver(
        ([entry]) => {
          const { isIntersecting } = entry;

          setIsVisible(isIntersecting);

          const hasChanged = vizRef.current !== isIntersecting;

          if (hasChanged) paramRef.current.onVisibilityChange?.(isIntersecting);
        },
        {
          rootMargin: `${bottomDistance}px`,
        },
      );

      obs.observe(element);

      ref.current = element;
      observer.current = obs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bottomDistance],
  );

  return useMemo(() => [isVisible, setRef], [isVisible, setRef]);
}
